import { useState, useEffect, useRef } from 'react';

type PromptContentPart = {
  type: 'text' | 'image_url';
  text?: string;
  image_url?: {
    url: string;
    detail?: 'low' | 'high' | 'auto';
  };
};

type PromptMessageContent = string | PromptContentPart[];

type PromptMessage = {
  role: 'system' | 'user' | 'assistant';
  content: PromptMessageContent;
};

/**
 * Debug entry for a single API call to external messaging platform.
 */
interface ApiCallDebugEntry {
  /** Paragraph index (0-based) */
  index: number;
  /** HTTP status code from the API response */
  status: number;
  /** Raw JSON response from the API */
  response: unknown;
  /** Message ID if successful */
  messageId?: string;
}

/**
 * Delivery debug data captured from messaging providers.
 */
interface DeliveryDebugData {
  /** Array of API call debug entries */
  apiCalls?: ApiCallDebugEntry[];
  /** Number of paragraphs sent */
  paragraphCount?: number;
  /** Array of message IDs for multi-paragraph messages */
  messageIds?: string[];
}

/**
 * Analytics data extracted from AI response.
 * Contains message classification, topics, and goal completion information.
 */
interface MessageAnalytics {
  /** Message type: win (breakthrough), struggle (difficulty), schema (belief), insight (realization) */
  message_classification?: string;
  /** The main topic or subject being discussed */
  key_topics?: string;
  /** Whether the user reached a goal, with the goal description if so */
  goals_reached?: string | null;
  /** What the user is trying to accomplish (question, request, statement, etc.) */
  intent?: string;
}

/**
 * Text model pricing (cents per 1M tokens) - mirrors pricing.ts from API
 * Used to calculate costs client-side for display purposes
 */
const TEXT_MODEL_PRICING: Record<string, { inputCentsPerMillion: number; outputCentsPerMillion: number }> = {
  'openai/gpt-4o': { inputCentsPerMillion: 250, outputCentsPerMillion: 1000 },
  'openai/gpt-4o-mini': { inputCentsPerMillion: 15, outputCentsPerMillion: 60 },
  'openai/gpt-4-turbo': { inputCentsPerMillion: 1000, outputCentsPerMillion: 3000 },
  'openai/gpt-4': { inputCentsPerMillion: 3000, outputCentsPerMillion: 6000 },
  'openai/gpt-3.5-turbo': { inputCentsPerMillion: 50, outputCentsPerMillion: 150 },
  'openai/o1': { inputCentsPerMillion: 1500, outputCentsPerMillion: 6000 },
  'openai/o1-mini': { inputCentsPerMillion: 300, outputCentsPerMillion: 1200 },
  'anthropic/claude-3.5-sonnet': { inputCentsPerMillion: 300, outputCentsPerMillion: 1500 },
  'anthropic/claude-3-opus': { inputCentsPerMillion: 1500, outputCentsPerMillion: 7500 },
  'anthropic/claude-3-sonnet': { inputCentsPerMillion: 300, outputCentsPerMillion: 1500 },
  'anthropic/claude-3-haiku': { inputCentsPerMillion: 25, outputCentsPerMillion: 125 },
  'google/gemini-pro': { inputCentsPerMillion: 50, outputCentsPerMillion: 150 },
  'google/gemini-pro-1.5': { inputCentsPerMillion: 125, outputCentsPerMillion: 500 },
  'google/gemini-flash-1.5': { inputCentsPerMillion: 7.5, outputCentsPerMillion: 30 },
  'google/gemini-3-flash-preview': { inputCentsPerMillion: 50, outputCentsPerMillion: 300 },
  'meta-llama/llama-3.1-405b-instruct': { inputCentsPerMillion: 270, outputCentsPerMillion: 270 },
  'meta-llama/llama-3.1-70b-instruct': { inputCentsPerMillion: 52, outputCentsPerMillion: 75 },
  'meta-llama/llama-3.1-8b-instruct': { inputCentsPerMillion: 5.5, outputCentsPerMillion: 5.5 },
  'mistralai/mistral-large': { inputCentsPerMillion: 200, outputCentsPerMillion: 600 },
  'deepseek/deepseek-chat': { inputCentsPerMillion: 14, outputCentsPerMillion: 28 },
};

// Unknown models show $0.00 to make missing pricing immediately visible
const UNKNOWN_PRICING = { inputCentsPerMillion: 0, outputCentsPerMillion: 0 };

/**
 * Normalizes model identifiers for lookup.
 * Handles variations in naming conventions between providers.
 */
function normalizeModelId(model: string): string {
  // Remove any version suffixes that might differ
  // Handles both formats:
  // - Dashed dates: 'openai/gpt-4o-2024-01-01' -> 'openai/gpt-4o'
  // - Compact dates: 'google/gemini-3-flash-preview-20251217' -> 'google/gemini-3-flash-preview'
  return model
    .replace(/-\d{4}-\d{2}-\d{2}$/, '')  // Remove -YYYY-MM-DD suffix
    .replace(/-\d{8}$/, '');              // Remove -YYYYMMDD suffix
}

/**
 * Gets text model pricing from lookup table with normalization fallback.
 * Returns zero pricing for unknown models to make missing pricing visible.
 */
function getTextModelPricing(model: string): { inputCentsPerMillion: number; outputCentsPerMillion: number } {
  // Try exact match first
  if (TEXT_MODEL_PRICING[model]) {
    return TEXT_MODEL_PRICING[model];
  }

  // Try normalized model ID
  const normalizedModel = normalizeModelId(model);
  if (normalizedModel !== model && TEXT_MODEL_PRICING[normalizedModel]) {
    return TEXT_MODEL_PRICING[normalizedModel];
  }

  // Return zero pricing for unknown models - makes missing pricing visible in UI
  console.warn(`[Pricing] Unknown model "${model}" - showing $0.00. Add to TEXT_MODEL_PRICING.`);
  return UNKNOWN_PRICING;
}

/**
 * TTS model pricing (cents per 1M characters) - mirrors pricing.ts from API
 */
const TTS_MODEL_PRICING: Record<string, { centsPerMillionChars: number }> = {
  'tts-1': { centsPerMillionChars: 1500 },
  'tts-1-hd': { centsPerMillionChars: 3000 },
};

const DEFAULT_TTS_PRICING = { centsPerMillionChars: 1500 };

/**
 * Calculate input token cost in cents
 */
function calculateInputCost(model: string, inputTokens: number): number {
  const pricing = getTextModelPricing(model);
  return (inputTokens / 1_000_000) * pricing.inputCentsPerMillion;
}

/**
 * Calculate output token cost in cents
 */
function calculateOutputCost(model: string, outputTokens: number): number {
  const pricing = getTextModelPricing(model);
  return (outputTokens / 1_000_000) * pricing.outputCentsPerMillion;
}

/**
 * Calculate TTS cost in cents based on character count
 */
function calculateTTSCost(model: string, characterCount: number): number {
  const pricing = TTS_MODEL_PRICING[model] ?? DEFAULT_TTS_PRICING;
  return (characterCount / 1_000_000) * pricing.centsPerMillionChars;
}

/**
 * Format cost in cents to a display string.
 * Always shows 4 decimal places for individual costs per user request.
 */
function formatCost(costCents: number): string {
  return `$${(costCents / 100).toFixed(4)}`;
}

/**
 * Message context containing introspection data from AI responses.
 */
interface MessageContext {
  model?: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
  /** Pre-calculated cost in cents from the backend (uses real-time OpenRouter pricing) */
  costCents?: number;
  systemPrompt?: string;
  /** The current user message that triggered the response */
  currentUserMessage?: string;
  /** Conversation history exactly as passed to AI (oldest to newest) */
  conversationHistory?: PromptMessage[];
  fullPrompt?: PromptMessage[];
  /** Structured system/tool metadata extracted from the assistant response */
  toolContext?: Record<string, unknown> | null;
  /** Total response generation time in milliseconds */
  latencyMs?: number;
  /** Time to first response in milliseconds (when first chunk was sent to user) */
  timeToFirstResponseMs?: number;
  source?: string;
  toolsExecuted?: Array<{ toolName: string; durationMs: number; success: boolean }>;
  /** Delivery debug data from messaging providers */
  deliveryDebug?: DeliveryDebugData;
  /** Per-message analytics extracted from AI response */
  analytics?: MessageAnalytics | null;
  /** TTS latency in milliseconds (for voice messages) */
  ttsLatencyMs?: number;
  /** Original text content that was converted to speech (for voice messages) */
  transcription?: string;
  /** URL to the audio file (for voice messages) */
  audioUrl?: string;
  [key: string]: unknown;
}

/**
 * Assembles the full prompt exactly as sent to the AI for debugging.
 * Order follows industry best practices:
 * 1. System Instructions
 * 2. Conversation History (oldest to newest)
 * 3. Latest User Message
 */
function assembleFullPrompt(context: MessageContext): PromptMessage[] {
  if (context.fullPrompt && context.fullPrompt.length > 0) {
    return context.fullPrompt;
  }

  const messages: PromptMessage[] = [];

  // 1. System prompt
  if (context.systemPrompt) {
    messages.push({ role: 'system', content: context.systemPrompt });
  }

  // 2. Conversation history (already in oldest to newest order)
  if (context.conversationHistory && context.conversationHistory.length > 0) {
    messages.push(...context.conversationHistory);
  }

  // 2.5. Hidden system/tool context is injected immediately before the
  // triggering user message in the real AI prompt flow.
  if (context.toolContext && Object.keys(context.toolContext).length > 0) {
    messages.push({ role: 'assistant', content: JSON.stringify(context.toolContext) });
  }

  // 3. Latest user message
  if (context.currentUserMessage) {
    messages.push({ role: 'user', content: context.currentUserMessage });
  }

  return messages;
}

/**
 * Message object that can be introspected.
 */
export interface IntrospectableMessage {
  id: string;
  direction: string | null;
  messageType: string | null;
  content: string | null;
  createdAt: string | null;
  context?: MessageContext | string | null;
  /** Message delivery status (e.g., sent, delivered, read, failed) */
  status?: string | null;
  user?: {
    id: string;
    name: string | null;
  } | null;
}

interface MessageIntrospectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  message: IntrospectableMessage | null;
}

/**
 * Safely parse stringified JSON context if necessary.
 */
function parseContext(context: MessageContext | string | null | undefined): MessageContext | null {
  if (!context) return null;

  if (typeof context === 'string') {
    try {
      return JSON.parse(context) as MessageContext;
    } catch {
      return null;
    }
  }

  return context;
}

/**
 * Format a date string for display.
 */
function formatDateTime(dateString: string | null): string {
  if (!dateString) return 'N/A';

  const date = new Date(dateString);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * Get styling for message status badge.
 */
function getStatusBadgeStyle(status: string | null | undefined): React.CSSProperties {
  if (!status) return { backgroundColor: '#f1f5f9', color: '#64748b' };

  switch (status.toLowerCase()) {
    case 'read':
      return { backgroundColor: '#dcfce7', color: '#166534' };
    case 'delivered':
      return { backgroundColor: '#dbeafe', color: '#1e40af' };
    case 'sent':
      return { backgroundColor: '#f1f5f9', color: '#64748b' };
    case 'failed':
      return { backgroundColor: '#fef2f2', color: '#dc2626' };
    case 'pending':
      return { backgroundColor: '#fef9c3', color: '#854d0e' };
    default:
      return { backgroundColor: '#f1f5f9', color: '#64748b' };
  }
}

/**
 * Get display label for message status.
 */
function getStatusLabel(status: string | null | undefined): string {
  if (!status) return 'Unknown';
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

/**
 * MessageIntrospectionModal component - A modal for viewing message introspection data.
 */
export function MessageIntrospectionModal({
  isOpen,
  onClose,
  message,
}: MessageIntrospectionModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [copyPromptSuccess, setCopyPromptSuccess] = useState(false);

  /**
   * Copy the full assembled prompt (exactly as sent to AI) to clipboard
   */
  const handleCopyFullPrompt = async () => {
    if (!message) return;

    const context = parseContext(message.context);
    if (!context) return;

    const fullPrompt = assembleFullPrompt(context);

    try {
      await navigator.clipboard.writeText(JSON.stringify(fullPrompt, null, 2));
      setCopyPromptSuccess(true);
      setTimeout(() => setCopyPromptSuccess(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = JSON.stringify(fullPrompt, null, 2);
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopyPromptSuccess(true);
      setTimeout(() => setCopyPromptSuccess(false), 2000);
    }
  };

  // Handle opening animation
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isVisible, onClose]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isVisible) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isVisible, onClose]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  if (!isVisible || !message) {
    return null;
  }

  const context = parseContext(message.context);
  const hasIntrospectionData = context && (
    context.model ||
    context.usage ||
    context.fullPrompt ||
    context.systemPrompt ||
    context.conversationHistory ||
    context.analytics
  );

  const isOutbound = message.direction === 'outbound';

  return (
    <div style={styles.overlay}>
      <div ref={modalRef} style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>Message Details</h2>
          <div style={styles.headerButtons}>
            {context && (context.fullPrompt || context.systemPrompt || context.conversationHistory || context.currentUserMessage) && (
              <button
                onClick={handleCopyFullPrompt}
                style={styles.copyPromptButton}
                aria-label="Copy full prompt to clipboard"
                title="Copy full prompt exactly as sent to AI"
              >
                {copyPromptSuccess ? 'Copied!' : 'Copy Full Prompt'}
              </button>
            )}
            <button
              onClick={onClose}
              style={styles.closeButton}
              aria-label="Close modal"
            >
              &times;
            </button>
          </div>
        </div>

        {/* Badges */}
        <div style={styles.badgesRow}>
          <span
            style={{
              ...styles.badge,
              backgroundColor: isOutbound ? '#dbeafe' : '#dcfce7',
              color: isOutbound ? '#1e40af' : '#166534',
            }}
          >
            {isOutbound ? 'Bot' : 'User'}
          </span>
          <span style={{ ...styles.badge, backgroundColor: '#f1f5f9', color: '#64748b' }}>
            {message.messageType}
          </span>
          {context?.model && (
            <span style={{ ...styles.badge, backgroundColor: '#f3e8ff', color: '#7c3aed' }}>
              {context.model}
            </span>
          )}
          {context?.timeToFirstResponseMs && (
            <span style={{ ...styles.badge, backgroundColor: '#d1fae5', color: '#059669' }} title="Time to first response">
              First: {context.timeToFirstResponseMs}ms
            </span>
          )}
          {context?.latencyMs && (
            <span style={{ ...styles.badge, backgroundColor: '#fef3c7', color: '#d97706' }} title="Total generation time">
              Total: {context.latencyMs}ms
            </span>
          )}
          {message.status && (
            <span style={{ ...styles.badge, ...getStatusBadgeStyle(message.status) }}>
              {getStatusLabel(message.status)}
            </span>
          )}
        </div>

        <div style={styles.content}>
          {/* Message info */}
          <div style={styles.infoGrid}>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>ID</span>
              <span style={styles.infoValue}>{message.id}</span>
            </div>
            <div style={styles.infoItem}>
              <span style={styles.infoLabel}>Created</span>
              <span style={styles.infoValue}>{formatDateTime(message.createdAt)}</span>
            </div>
            {message.user && (
              <div style={styles.infoItem}>
                <span style={styles.infoLabel}>User</span>
                <span style={styles.infoValue}>{message.user.name || message.user.id}</span>
              </div>
            )}
          </div>

          {/* Content section */}
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Content</h3>
            <div style={styles.codeBlock}>
              <pre style={styles.pre}>{message.content}</pre>
            </div>
          </div>

          {/* Introspection data sections */}
          {hasIntrospectionData ? (
            <>
              {/* Message Analytics */}
              {context.analytics && (
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Message Analytics</h3>
                  <div style={styles.analyticsGrid}>
                    {context.analytics.message_classification && (
                      <div style={styles.analyticsItem}>
                        <div style={styles.analyticsLabel}>Classification</div>
                        <span
                          style={{
                            ...styles.analyticsBadge,
                            backgroundColor:
                              context.analytics.message_classification === 'win'
                                ? '#dcfce7'
                                : context.analytics.message_classification === 'struggle'
                                  ? '#fef2f2'
                                  : context.analytics.message_classification === 'schema'
                                    ? '#fef9c3'
                                    : context.analytics.message_classification === 'insight'
                                      ? '#f5f3ff'
                                      : '#f1f5f9',
                            color:
                              context.analytics.message_classification === 'win'
                                ? '#166534'
                                : context.analytics.message_classification === 'struggle'
                                  ? '#dc2626'
                                  : context.analytics.message_classification === 'schema'
                                    ? '#854d0e'
                                    : context.analytics.message_classification === 'insight'
                                      ? '#7c3aed'
                                      : '#64748b',
                            border:
                              context.analytics.message_classification === 'win'
                                ? '1px solid #bbf7d0'
                                : context.analytics.message_classification === 'struggle'
                                  ? '1px solid #fecaca'
                                  : context.analytics.message_classification === 'schema'
                                    ? '1px solid #fde68a'
                                    : context.analytics.message_classification === 'insight'
                                      ? '1px solid #ddd6fe'
                                      : '1px solid #e2e8f0',
                            letterSpacing: '0.05em',
                          }}
                        >
                          {context.analytics.message_classification.toUpperCase()}
                        </span>
                      </div>
                    )}
                    {context.analytics.intent && (
                      <div style={styles.analyticsItem}>
                        <div style={styles.analyticsLabel}>Intent</div>
                        <span style={{ ...styles.analyticsBadge, backgroundColor: '#dbeafe', color: '#1e40af' }}>
                          {context.analytics.intent}
                        </span>
                      </div>
                    )}
                    {context.analytics.key_topics && (
                      <div style={{ ...styles.analyticsItem, gridColumn: 'span 2' }}>
                        <div style={styles.analyticsLabel}>Topic</div>
                        <span style={styles.analyticsText}>{context.analytics.key_topics}</span>
                      </div>
                    )}
                    {context.analytics.goals_reached && (
                      <div style={{ ...styles.analyticsItem, gridColumn: 'span 2' }}>
                        <div style={styles.analyticsLabel}>Goal Reached</div>
                        <span style={{ ...styles.analyticsBadge, backgroundColor: '#f3e8ff', color: '#7c3aed' }}>
                          {context.analytics.goals_reached}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Token Cost with Expense Breakdown */}
              {context.usage && (
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Token Cost</h3>
                  <div style={styles.tokenCostGrid}>
                    <div style={styles.tokenCostItem}>
                      <div style={styles.tokenCostValue}>{context.usage.promptTokens.toLocaleString()}</div>
                      <div style={styles.tokenCostLabel}>Input Tokens</div>
                    </div>
                    <div style={styles.tokenCostItem}>
                      <div style={styles.tokenCostValue}>{context.usage.completionTokens.toLocaleString()}</div>
                      <div style={styles.tokenCostLabel}>Output Tokens</div>
                    </div>
                  </div>
                  {/* Total cost - use pre-calculated from backend when available */}
                  <div style={styles.totalCostRow}>
                    <span style={styles.totalCostLabel}>Total Text Generation Cost:</span>
                    <span style={styles.totalCostValue}>
                      {context.costCents !== undefined
                        ? formatCost(context.costCents)
                        : formatCost(
                            calculateInputCost(context.model || '', context.usage.promptTokens) +
                            calculateOutputCost(context.model || '', context.usage.completionTokens)
                          )
                      }
                    </span>
                  </div>
                  {context.costCents === undefined && (
                    <div style={styles.costWarning}>
                      Cost calculated client-side (legacy message). New messages use server-side pricing.
                    </div>
                  )}
                </div>
              )}

              {/* Voice Response (TTS) Cost */}
              {context.ttsLatencyMs && context.transcription && (
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Voice Response Cost</h3>
                  <div style={styles.tokenCostGrid}>
                    <div style={styles.tokenCostItem}>
                      <div style={styles.tokenCostValue}>{context.transcription.length.toLocaleString()}</div>
                      <div style={styles.tokenCostLabel}>Characters</div>
                    </div>
                    <div style={styles.tokenCostItem}>
                      <div style={{ ...styles.tokenCostValue, color: '#7c3aed' }}>
                        {formatCost(calculateTTSCost('tts-1', context.transcription.length))}
                      </div>
                      <div style={styles.tokenCostLabel}>TTS Cost (tts-1)</div>
                    </div>
                  </div>
                  <div style={styles.ttsInfoRow}>
                    <span style={styles.ttsInfoLabel}>Generation Time:</span>
                    <span style={styles.ttsInfoValue}>{context.ttsLatencyMs.toLocaleString()}ms</span>
                  </div>
                </div>
              )}

              {/* Response Timing */}
              {(context.timeToFirstResponseMs || context.latencyMs) && (
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Response Timing</h3>
                  <div style={styles.tokenCostGrid}>
                    {context.timeToFirstResponseMs && (
                      <div style={styles.tokenCostItem}>
                        <div style={{ ...styles.tokenCostValue, color: '#059669' }}>{context.timeToFirstResponseMs.toLocaleString()}</div>
                        <div style={styles.tokenCostLabel}>Time to First Response (ms)</div>
                      </div>
                    )}
                    {context.latencyMs && (
                      <div style={styles.tokenCostItem}>
                        <div style={{ ...styles.tokenCostValue, color: '#d97706' }}>{context.latencyMs.toLocaleString()}</div>
                        <div style={styles.tokenCostLabel}>Total Generation Time (ms)</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Full Assembled Prompt - exactly as sent to AI */}
              {(context.fullPrompt || context.systemPrompt || context.conversationHistory || context.currentUserMessage) && (
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>
                    Full Prompt (as sent to AI)
                  </h3>
                  <p style={styles.promptDescription}>
                    This is the exact stored prompt payload sent to the AI, shown without alteration for debugging.
                  </p>
                  <div style={{ ...styles.codeBlock, maxHeight: '300px' }}>
                    <pre style={styles.pre}>
                      {JSON.stringify(assembleFullPrompt(context), null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* Current User Message */}
              {context.currentUserMessage && (
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Current User Message</h3>
                  <div style={styles.currentMessageBlock}>
                    <pre style={styles.pre}>{context.currentUserMessage}</pre>
                  </div>
                </div>
              )}

              {/* System Prompt */}
              {context.systemPrompt && (
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>System Prompt</h3>
                  <div style={{ ...styles.codeBlock, maxHeight: '200px' }}>
                    <pre style={styles.pre}>{context.systemPrompt}</pre>
                  </div>
                </div>
              )}

              {/* Conversation History */}
              {context.conversationHistory && context.conversationHistory.length > 0 && (
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>
                    Conversation History ({context.conversationHistory.length} messages, oldest first)
                  </h3>
                  <div style={{ ...styles.codeBlock, maxHeight: '200px' }}>
                    <pre style={styles.pre}>
                      {JSON.stringify(context.conversationHistory, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* Tools Executed */}
              {context.toolsExecuted && context.toolsExecuted.length > 0 && (
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Tools Executed</h3>
                  <div style={styles.toolsList}>
                    {context.toolsExecuted.map((tool, index) => (
                      <div key={index} style={styles.toolItem}>
                        <span style={styles.toolName}>{tool.toolName}</span>
                        <span style={styles.toolDuration}>{tool.durationMs}ms</span>
                        <span
                          style={{
                            ...styles.toolStatus,
                            backgroundColor: tool.success ? '#dcfce7' : '#fef2f2',
                            color: tool.success ? '#166534' : '#dc2626',
                          }}
                        >
                          {tool.success ? 'Success' : 'Failed'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Delivery Debug Data */}
              {context.deliveryDebug && (
                <div style={styles.section}>
                  <h3 style={styles.sectionTitle}>Delivery Debug Data</h3>
                  <p style={styles.deliveryDebugDescription}>
                    Technical details about message delivery to external messaging platforms.
                  </p>

                  {/* Summary info */}
                  <div style={styles.deliveryDebugSummary}>
                    {context.deliveryDebug.paragraphCount !== undefined && (
                      <div style={styles.deliveryDebugItem}>
                        <span style={styles.deliveryDebugLabel}>Paragraphs Sent</span>
                        <span style={styles.deliveryDebugValue}>{context.deliveryDebug.paragraphCount}</span>
                      </div>
                    )}
                    {context.deliveryDebug.messageIds && context.deliveryDebug.messageIds.length > 0 && (
                      <div style={styles.deliveryDebugItem}>
                        <span style={styles.deliveryDebugLabel}>Message IDs</span>
                        <span style={styles.deliveryDebugValue}>{context.deliveryDebug.messageIds.length}</span>
                      </div>
                    )}
                    {context.deliveryDebug.apiCalls && context.deliveryDebug.apiCalls.length > 0 && (
                      <div style={styles.deliveryDebugItem}>
                        <span style={styles.deliveryDebugLabel}>API Calls</span>
                        <span style={styles.deliveryDebugValue}>{context.deliveryDebug.apiCalls.length}</span>
                      </div>
                    )}
                  </div>

                  {/* API Calls Details */}
                  {context.deliveryDebug.apiCalls && context.deliveryDebug.apiCalls.length > 0 && (
                    <div style={styles.apiCallsList}>
                      <h4 style={styles.apiCallsTitle}>API Call Details</h4>
                      {context.deliveryDebug.apiCalls.map((call, index) => (
                        <div key={index} style={styles.apiCallItem}>
                          <div style={styles.apiCallHeader}>
                            <span style={styles.apiCallIndex}>Chunk {call.index + 1}</span>
                            <span
                              style={{
                                ...styles.apiCallStatus,
                                backgroundColor: call.status >= 200 && call.status < 300 ? '#dcfce7' : '#fef2f2',
                                color: call.status >= 200 && call.status < 300 ? '#166534' : '#dc2626',
                              }}
                            >
                              HTTP {call.status}
                            </span>
                            {call.messageId && (
                              <span style={styles.apiCallMessageId}>ID: {call.messageId}</span>
                            )}
                          </div>
                          {call.response !== undefined && call.response !== null && (
                            <div style={styles.apiCallResponse}>
                              <pre style={styles.pre}>
                                {JSON.stringify(call.response, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Message IDs */}
                  {context.deliveryDebug.messageIds && context.deliveryDebug.messageIds.length > 0 && (
                    <div style={styles.messageIdsList}>
                      <h4 style={styles.apiCallsTitle}>Platform Message IDs</h4>
                      <div style={styles.messageIdsGrid}>
                        {context.deliveryDebug.messageIds.map((msgId, index) => (
                          <span key={index} style={styles.messageIdBadge}>
                            {msgId}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

            </>
          ) : (
            <div style={styles.emptyState}>
              <span style={styles.emptyIcon}>&#x1F50D;</span>
              <p style={styles.emptyText}>No introspection data available for this message.</p>
              <p style={styles.emptySubtext}>
                Introspection data is typically available for bot-generated responses.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button onClick={onClose} style={styles.closeButtonFooter}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '640px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
    flexShrink: 0,
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#3D3A36',
    margin: 0,
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '24px',
    color: '#9B9285',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '4px',
    lineHeight: 1,
  },
  badgesRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    padding: '12px 24px',
    borderBottom: '1px solid rgba(93, 86, 78, 0.08)',
  },
  badge: {
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 500,
  },
  content: {
    padding: '20px 24px',
    overflowY: 'auto',
    flex: 1,
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '12px',
    marginBottom: '20px',
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  infoLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#9B9285',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  infoValue: {
    fontSize: '13px',
    color: '#3D3A36',
    fontFamily: 'monospace',
    wordBreak: 'break-all',
  },
  section: {
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#3D3A36',
    marginBottom: '10px',
    margin: '0 0 10px 0',
  },
  codeBlock: {
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    padding: '14px',
    overflowY: 'auto',
    border: '1px solid rgba(93, 86, 78, 0.06)',
  },
  pre: {
    margin: 0,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontSize: '12px',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    color: '#5C564E',
    lineHeight: 1.5,
  },
  tokenGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    padding: '16px',
    border: '1px solid rgba(93, 86, 78, 0.06)',
  },
  tokenItem: {
    textAlign: 'center',
  },
  tokenValue: {
    fontSize: '24px',
    fontWeight: 600,
    color: '#6366f1',
    marginBottom: '4px',
  },
  tokenLabel: {
    fontSize: '11px',
    color: '#9B9285',
    textTransform: 'uppercase',
  },
  toolsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  toolItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 14px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid rgba(93, 86, 78, 0.06)',
  },
  toolName: {
    flex: 1,
    fontSize: '13px',
    fontWeight: 500,
    color: '#3D3A36',
  },
  toolDuration: {
    fontSize: '12px',
    color: '#9B9285',
  },
  toolStatus: {
    padding: '3px 8px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  emptyState: {
    textAlign: 'center',
    padding: '32px 16px',
  },
  emptyIcon: {
    fontSize: '32px',
    marginBottom: '12px',
    display: 'block',
  },
  emptyText: {
    fontSize: '14px',
    color: '#5C564E',
    margin: '0 0 4px 0',
  },
  emptySubtext: {
    fontSize: '12px',
    color: '#9B9285',
    margin: 0,
  },
  footer: {
    padding: '16px 24px',
    borderTop: '1px solid rgba(93, 86, 78, 0.08)',
    flexShrink: 0,
  },
  closeButtonFooter: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#f8fafc',
    color: '#5C564E',
    border: '1px solid rgba(93, 86, 78, 0.1)',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
  },
  headerButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  copyPromptButton: {
    backgroundColor: '#faf5ff',
    border: '1px solid #e9d5ff',
    color: '#7c3aed',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
  },
  promptDescription: {
    fontSize: '12px',
    color: '#64748b',
    margin: '0 0 10px 0',
    fontStyle: 'italic',
  },
  tokenCostGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    padding: '16px',
    border: '1px solid rgba(93, 86, 78, 0.06)',
  },
  tokenCostGridFour: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '12px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    padding: '16px',
    border: '1px solid rgba(93, 86, 78, 0.06)',
  },
  tokenCostItem: {
    textAlign: 'center',
  },
  tokenCostValue: {
    fontSize: '24px',
    fontWeight: 600,
    color: '#6366f1',
    marginBottom: '4px',
  },
  tokenCostLabel: {
    fontSize: '11px',
    color: '#9B9285',
    textTransform: 'uppercase',
  },
  totalCostRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '12px',
    padding: '10px 16px',
    backgroundColor: '#f0fdf4',
    borderRadius: '8px',
    border: '1px solid #bbf7d0',
  },
  totalCostLabel: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#166534',
  },
  totalCostValue: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#15803d',
  },
  costWarning: {
    marginTop: '8px',
    padding: '8px 12px',
    backgroundColor: '#fef9c3',
    borderRadius: '6px',
    fontSize: '11px',
    color: '#854d0e',
    fontStyle: 'italic',
  },
  ttsInfoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '12px',
    padding: '10px 16px',
    backgroundColor: '#faf5ff',
    borderRadius: '8px',
    border: '1px solid #e9d5ff',
  },
  ttsInfoLabel: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#7c3aed',
  },
  ttsInfoValue: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#6d28d9',
  },
  currentMessageBlock: {
    backgroundColor: '#eff6ff',
    borderRadius: '8px',
    padding: '14px',
    overflowY: 'auto',
    border: '1px solid #bfdbfe',
    maxHeight: '100px',
  },
  // Delivery Debug Data styles
  deliveryDebugDescription: {
    fontSize: '12px',
    color: '#64748b',
    margin: '0 0 12px 0',
    fontStyle: 'italic',
  },
  deliveryDebugSummary: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    backgroundColor: '#f0fdf4',
    borderRadius: '8px',
    padding: '12px',
    border: '1px solid #bbf7d0',
    marginBottom: '16px',
  },
  deliveryDebugItem: {
    textAlign: 'center',
  },
  deliveryDebugLabel: {
    display: 'block',
    fontSize: '10px',
    color: '#166534',
    textTransform: 'uppercase',
    marginBottom: '4px',
  },
  deliveryDebugValue: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#15803d',
  },
  apiCallsList: {
    marginTop: '12px',
  },
  apiCallsTitle: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#5C564E',
    marginBottom: '8px',
    margin: '0 0 8px 0',
  },
  apiCallItem: {
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '8px',
    border: '1px solid rgba(93, 86, 78, 0.08)',
  },
  apiCallHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '8px',
  },
  apiCallIndex: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#3D3A36',
  },
  apiCallStatus: {
    padding: '3px 8px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: 600,
  },
  apiCallMessageId: {
    fontSize: '11px',
    color: '#64748b',
    fontFamily: 'monospace',
  },
  apiCallResponse: {
    backgroundColor: '#ffffff',
    borderRadius: '6px',
    padding: '10px',
    border: '1px solid rgba(93, 86, 78, 0.06)',
    maxHeight: '120px',
    overflowY: 'auto',
  },
  messageIdsList: {
    marginTop: '16px',
  },
  messageIdsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  messageIdBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    backgroundColor: '#e0f2fe',
    color: '#0369a1',
    borderRadius: '4px',
    fontSize: '11px',
    fontFamily: 'monospace',
  },
  // Message Analytics styles
  analyticsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    padding: '16px',
    border: '1px solid rgba(93, 86, 78, 0.06)',
  },
  analyticsItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  analyticsLabel: {
    fontSize: '10px',
    color: '#9B9285',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  analyticsBadge: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 500,
  },
  analyticsText: {
    fontSize: '13px',
    color: '#3D3A36',
  },
};

export default MessageIntrospectionModal;
